/**
 * Collection grid: paginate the CARDS, not the products.
 *
 * Shopify breaks pages on products, but the grid draws cards and one product can
 * contribute several colourway cards. A 12-product page rendered about 20 cards,
 * 20 does not divide by the column count, and the last row was left with holes
 * while later pages still had stock — it read as the end of the range.
 *
 * The section now renders a block of up to 50 products, and this cuts those cards
 * into fixed pages so every page is a whole number of rows. Only the final page of
 * a collection can be short, which is honest because there is nothing after it.
 *
 * Every card stays in the DOM, so crawlers and no-JS visitors still get the whole
 * block plus the server's own numbered links, which this replaces only when JS runs.
 */
(function () {
  'use strict';

  var state = { cards: [], perPage: 12, page: 1, pages: 1 };

  /* Used by the collection grid and the search results grid. Both mark themselves
     with data-cards-per-page, so bind to that rather than to one hardcoded id.
     #product-grid stays as a fallback in case a section sets the id but not the
     attribute. */
  function grid() {
    return document.querySelector('[data-cards-per-page]') || document.getElementById('product-grid');
  }

  /* .pagination carries display:flex from a class, and a class beats the [hidden]
     attribute, so the server pager has to be hidden with an inline style.

     Find it by data-server-pagination, never by a styling class. The search section
     styles its nav with utilities and carries no .pagination, so the old lookup
     returned null there and the server pager was left on screen underneath the card
     pager. The class is kept as a fallback for any surface not yet carrying the hook,
     and the card pager we build ourselves is excluded either way. */
  function serverNav() {
    return document.querySelector('[data-server-pagination]') ||
           document.querySelector('.pagination:not([data-card-pager])');
  }

  /* The link into Shopify's next block of products. Scoped to the server nav because
     once our own .pagination host exists a document-wide lookup can match that
     instead, and it holds no page= links, which would silently drop the handoff. */
  function serverNextLink(nav) {
    if (!nav) return null;
    var links = nav.querySelectorAll('a[href*="page="]');
    for (var i = links.length - 1; i >= 0; i--) {
      if (/next/i.test(links[i].textContent || '')) return links[i];
    }
    return null;
  }

  /* collection-page.js filters by stamping data-filtered="hidden", which CSS hides
     with !important. Those cards must be excluded from the count, or filtering to
     5 results still builds a 3 page pager whose pages show 2 cards, then 3, then
     none. Paginate what is actually eligible, not everything rendered. */
  function eligible() {
    return state.cards.filter(function (c) { return !c.hasAttribute('data-filtered'); });
  }

  function filtersActive() {
    return !!document.querySelector('.product-card[data-filtered]');
  }

  function render() {
    var cards = eligible();
    state.pages = Math.max(1, Math.ceil(cards.length / state.perPage));
    if (state.page > state.pages) state.page = state.pages;

    var start = (state.page - 1) * state.perPage;
    var visible = cards.slice(start, start + state.perPage);
    state.cards.forEach(function (card) {
      card.style.display = visible.indexOf(card) !== -1 ? '' : 'none';
    });
    buildPager();
    syncCounter();
  }

  function go(page, scroll) {
    state.page = Math.min(Math.max(1, page), state.pages);
    render();
    if (scroll) {
      var g = grid();
      if (g) window.scrollTo({ top: g.getBoundingClientRect().top + window.pageYOffset - 120, behavior: 'smooth' });
    }
    /* Keep the URL shareable without a reload. */
    try {
      var u = new URL(window.location.href);
      if (state.page > 1) u.searchParams.set('gp', String(state.page));
      else u.searchParams.delete('gp');
      window.history.replaceState({}, '', u.toString());
    } catch (e) { /* older browsers just lose the deep link */ }
  }

  function tile(label, opts) {
    var el = document.createElement(opts.current || opts.plain ? 'span' : 'button');
    if (el.tagName === 'BUTTON') el.type = 'button';
    el.className = opts.step ? 'pagination-step' : 'pagination-tile';
    if (opts.current) {
      el.className += ' is-current';
      el.setAttribute('aria-current', 'page');
    }
    el.textContent = label;
    if (opts.onClick) el.addEventListener('click', opts.onClick);
    return el;
  }

  function buildPager() {
    var host = document.querySelector('[data-card-pager]');
    if (!host) {
      var g = grid();
      if (!g) return;
      host = document.createElement('nav');
      host.className = 'pagination animate-fade-up';
      host.setAttribute('aria-label', 'Pagination');
      host.setAttribute('data-card-pager', '');
      g.parentNode.insertBefore(host, g.nextSibling);
    }
    host.innerHTML = '';
    if (state.pages < 2) { host.style.display = 'none'; return; }
    host.style.display = '';

    if (state.page > 1) {
      host.appendChild(tile('Previous', { step: true, onClick: function () { go(state.page - 1, true); } }));
    }
    for (var p = 1; p <= state.pages; p++) {
      (function (n) {
        host.appendChild(
          n === state.page
            ? tile(String(n), { current: true })
            : tile(String(n), { onClick: function () { go(n, true); } })
        );
      })(p);
    }
    if (state.page < state.pages) {
      host.appendChild(tile('Next', { step: true, onClick: function () { go(state.page + 1, true); } }));
    } else {
      /* Out of cards in this block. If Shopify has another block of products,
         hand off to it with a real link so nothing is unreachable. */
      var nav = serverNav();
      var next = serverNextLink(nav);
      if (nav && next) {
        var a = document.createElement('a');
        a.className = 'pagination-step';
        a.href = next.getAttribute('href');
        a.textContent = 'Next';
        host.appendChild(a);
      }
    }
  }

  /* The server counter counts products; what is on screen is cards. Say cards.
     collection-page.js re-renders #collection-toolbar after this script has run,
     which put the server's product figure back on first load, so watch the counter's
     parent and re-apply. The text comparison stops the observer retriggering itself. */
  var counterObserver = null;

  function counterEl() {
    var nodes = document.querySelectorAll('span, p, div');
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].children.length && /^Showing\b/.test((nodes[i].textContent || '').trim())) return nodes[i];
    }
    return null;
  }

  /* Search words its count differently ("12 results for 'sofa'") and the number is
     products, not cards. Once a product splits into colourways the grid shows more
     tiles than that, so the figure has to follow the cards. The section wraps just
     the "N result(s)" part in [data-card-count] for this. */
  function syncSearchCount() {
    var el = document.querySelector('[data-card-count]');
    if (!el) return false;
    var n = state.cards.length;
    var want = n + ' result' + (n === 1 ? '' : 's');
    if (el.textContent.trim() !== want) el.textContent = want;
    return true;
  }

  function syncCounter() {
    if (!state.cards.length) return;
    /* With a filter on, collection-page.js owns this text ("Showing 5 of 17").
       Writing ours too would just be two scripts overwriting each other. */
    if (filtersActive()) return;
    if (syncSearchCount()) return;
    var el = counterEl();
    if (!el) return;
    var from = (state.page - 1) * state.perPage + 1;
    var to = Math.min(state.page * state.perPage, state.cards.length);
    var want = 'Showing ' + from + '–' + to + ' of ' + state.cards.length;
    if (el.textContent.trim() !== want) el.textContent = want;

    if (!counterObserver && el.parentElement && window.MutationObserver) {
      counterObserver = new MutationObserver(function () { syncCounter(); });
      counterObserver.observe(el.parentElement, { childList: true, subtree: true, characterData: true });
    }
  }

  function init() {
    var g = grid();
    if (!g) return;
    var cards = [].slice.call(g.querySelectorAll('.product-card'));
    if (!cards.length) return;

    var perPage = parseInt(g.getAttribute('data-cards-per-page'), 10) || 12;
    /* Nothing to do when the whole block already fits on one page. */
    if (cards.length <= perPage) return;

    state.cards = cards;
    state.perPage = perPage;
    state.pages = Math.ceil(cards.length / perPage);

    var nav = serverNav();
    if (nav && !nav.hasAttribute('data-card-pager')) nav.style.display = 'none';

    var start = 1;
    try {
      var gp = parseInt(new URL(window.location.href).searchParams.get('gp'), 10);
      if (gp > 0) start = gp;
    } catch (e) { /* default to page 1 */ }

    state.page = Math.min(start, state.pages);
    render();

    /* Re-paginate whenever a filter is applied or cleared. Watching only
       data-filtered means our own style.display writes cannot retrigger this. */
    if (window.MutationObserver && !g.dataset.cardPagerWatched) {
      g.dataset.cardPagerWatched = '1';
      var pending = null;
      new MutationObserver(function () {
        clearTimeout(pending);
        pending = setTimeout(function () { state.page = 1; render(); }, 40);
      }).observe(g, { subtree: true, attributes: true, attributeFilter: ['data-filtered'] });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
